import React from 'react'
import { OmSpinner, omNotification } from '@owma/kit'

import i18n from 'i18n'

import EmailEditor from 'pages/EmailEditor'
import SendHelper from 'pages/main/components/SendHelper'

import type { PopupChildMessage } from './types'
import { consumePopupSendHelper, POPUP_WIN_ID_PARAM } from './handoff'

interface EmailEditorWindowState {
  sendHelper: SendHelper | null
  // opener/handoff 부재로 처음부터 편집을 시작할 수 없는 상태(전체화면 안내).
  initFailed: boolean
  // 편집 시작 후 opener 가 닫힌 상태(에디터는 유지하고 상단 배너로 경고 — 작성 내용 보존).
  openerClosed: boolean
}

function isOpenerAvailable(): boolean {
  const opener = window.opener as Window | null
  return opener != null && opener.closed !== true
}

function postToOpener(message: PopupChildMessage): void {
  if (!isOpenerAvailable()) {
    return
  }
  ;(window.opener as Window).postMessage(message, window.location.origin)
}

// opener 종료 감지 폴링 주기(ms). storage/postMessage 로는 opener close 를 신뢰성 있게 못 받으므로
// 저비용 폴링으로 감지하고, 감지 즉시 인터벌을 해제한다.
const OPENER_WATCH_INTERVAL_MS = 1000

class EmailEditorWindow extends React.Component<{}, EmailEditorWindowState> {
  state: EmailEditorWindowState = {
    sendHelper: null,
    initFailed: false,
    openerClosed: false,
  }

  emailEditorRef = React.createRef<any>()

  openerWatchTimer: number | null = null

  componentDidMount(): void {
    // opener 가 없으면 handoff 자체가 불가하므로 안내만 표시한다.
    if (!isOpenerAvailable()) {
      this.setState({ initFailed: true })
      return
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // openSendMailWindow handoff 경로: winId 로 opener 레지스트리의 sendHelper 를 꺼내
    // 바로 렌더한다(new/reply/forward 컨텍스트 포함). popup 은 opener 의 dataMgr 로만 동작한다.
    const winId = new URLSearchParams(window.location.search).get(POPUP_WIN_ID_PARAM)
    if (winId == null) {
      // handoff 없이 열린 경우(직접 URL 접근 등)는 지원하지 않는다(handoff-only).
      this.setState({ initFailed: true })
      return
    }

    const entry = consumePopupSendHelper(winId)
    if (entry == null) {
      // winId 는 있으나 handoff 를 못 찾음(popup 새로고침/opener 만료) → 안내.
      this.setState({ initFailed: true })
      return
    }
    if (entry.title != null && entry.title.length > 0) {
      document.title = entry.title
    }
    this.setState({ sendHelper: entry.sendHelper })

    // handoff 성공 후부터 opener 종료를 감시한다. opener realm(=sendHelper.dataMgr)이 사라지면
    // 발송/저장이 불가하므로 사용자에게 내용 보존을 안내한다. (CRITICAL 2 추가 방어)
    this.startOpenerWatch()
  }

  componentWillUnmount(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.stopOpenerWatch()
    // popup realm 안에서 SendHelper 가 띄운 antd notification 의 DOM 잔여만 정리한다(opener 무관).
    omNotification.destroy()
  }

  startOpenerWatch = (): void => {
    if (this.openerWatchTimer != null) {
      return
    }
    this.openerWatchTimer = window.setInterval(() => {
      if (!isOpenerAvailable()) {
        this.setState({ openerClosed: true })
        this.stopOpenerWatch()
      }
    }, OPENER_WATCH_INTERVAL_MS)
  }

  stopOpenerWatch = (): void => {
    if (this.openerWatchTimer != null) {
      window.clearInterval(this.openerWatchTimer)
      this.openerWatchTimer = null
    }
  }

  handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    // 작성 중인 초안이 있으면 draft 저장을 시도하고 브라우저 확인 다이얼로그를 띄운다.
    const editor = this.emailEditorRef.current
    if (editor != null && editor.needSave() === true) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  onSended = (res?: any): void => {
    // window.close() 전에 notification DOM 을 정리한다.
    omNotification.destroy()
    // EmailForm 이 resolve 하는 실제 형태는 { mailData, prevDraft } 다.
    // 신규 메일은 threadId/emailId 가 undefined 일 수 있다 (서버 생성 id 를 되쓰지 않음).
    const threadId: string | undefined = res?.mailData?.threadId ?? res?.prevDraft?.data?.threadId
    const emailId: string | undefined = res?.prevDraft?.data?.id
    postToOpener({ type: 'sended', threadId, emailId })
    window.close()
  }

  onCanceled = (): void => {
    omNotification.destroy()
    postToOpener({ type: 'canceled' })
    window.close()
  }

  render(): React.ReactNode {
    const { sendHelper, initFailed, openerClosed } = this.state

    if (initFailed) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>{i18n.t('email.popup.openerLost.title')}</p>
          <button type="button" onClick={() => window.close()}>
            {i18n.t('email.popup.openerLost.close')}
          </button>
        </div>
      )
    }

    // handoff 로 sendHelper 를 받기 전과 EmailEditor 의 startLoading 지연 구간에
    // 빈 화면 대신 스피너를 보여 준다.
    if (sendHelper == null) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          <OmSpinner size="large" />
        </div>
      )
    }

    return (
      <React.Fragment>
        {openerClosed ? (
          <div
            role="alert"
            style={{
              padding: '8px 16px',
              background: '#fff1f0',
              color: '#a8071a',
              borderBottom: '1px solid #ffa39e',
              fontSize: 13,
            }}
          >
            {i18n.t('email.popup.openerClosed.banner')}
          </div>
        ) : null}
        <EmailEditor
          ref={this.emailEditorRef}
          helper={sendHelper as any}
          myWin={window}
          isReady={true}
          onSended={this.onSended}
          onCanceled={this.onCanceled}
          onSetIFrame={(iframe: HTMLIFrameElement) => {
            // ngw 는 inline 마운트라 iframe 이 없다. tinymce 회귀 시에만 의미 있음
            // (그 경우 popup document 기준 copyStyles 재적용 필요 — 별도 change).
            void iframe
          }}
        />
      </React.Fragment>
    )
  }
}

export default EmailEditorWindow
